import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { ToastService } from '../../../../core/services/toast.service';
import {
  BreadcrumbItem,
  BreadcrumbsComponent,
} from '../../../../shared/components/breadcrumbs/breadcrumbs.component';
import {
  CLIENT_ETHNICITY_2020_OPTIONS,
  CLIENT_GENDER_OPTIONS,
  CLIENT_RACE_2020_OPTIONS,
  ClientDetailApiModel,
  ClientEthnicity2020,
  ClientGender,
  ClientRace2020,
} from '../../models/client.models';
import { ClientsService } from '../../services/clients.service';

@Component({
  selector: 'app-client-profile',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, BreadcrumbsComponent],
  templateUrl: './client-profile.component.html',
  styleUrl: './client-profile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientProfileComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly clientsService = inject(ClientsService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);

  readonly clientId = Number(this.route.snapshot.paramMap.get('id'));

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly client = signal<ClientDetailApiModel | null>(null);
  readonly genderOptions = CLIENT_GENDER_OPTIONS;
  readonly raceOptions = CLIENT_RACE_2020_OPTIONS;
  readonly ethnicityOptions = CLIENT_ETHNICITY_2020_OPTIONS;

  readonly isAdmin = computed(() => this.authService.role() === 'admin');

  readonly displayName = computed(() => {
    const client = this.client();
    if (!client) {
      return 'Client';
    }

    const parts = [client.firstName, client.middleName, client.lastName]
      .map((part) => (part ?? '').trim())
      .filter(Boolean);

    return parts.join(' ') || 'Client';
  });

  readonly breadcrumbItems = computed<BreadcrumbItem[]>(() => {
    const id = this.clientId;
    return [
      { label: 'Home', route: '/home' },
      { label: this.displayName(), route: `/clients/${id}` },
      { label: 'Profile' },
    ];
  });

  readonly form = this.fb.nonNullable.group({
    clientUniqueId: ['', [Validators.required, Validators.maxLength(64)]],
    enrollmentDate: ['', [Validators.required]],
    firstName: ['', [Validators.required, Validators.maxLength(100)]],
    middleName: ['', [Validators.maxLength(100)]],
    lastName: ['', [Validators.required, Validators.maxLength(100)]],
    birthdate: ['', [Validators.required]],
    reasonForInitialContact: ['', [Validators.required, Validators.maxLength(500)]],
    phone: ['', [Validators.maxLength(30)]],
    email: ['', [Validators.email, Validators.maxLength(255)]],
    race2020: [''],
    ethnicity2020: [''],
    gender: [''],
    genderOtherText: ['', [Validators.maxLength(120)]],
    zipCode: ['', [Validators.maxLength(20)]],
    consentOnFileToReferOut: ['', [Validators.required]],
    consentOnFileToReferOutDate: [''],
  });

  constructor() {
    this.loadClient();
  }

  goBackToClient(): void {
    if (!this.clientId || Number.isNaN(this.clientId)) {
      void this.router.navigate(['/home']);
      return;
    }

    void this.router.navigate(['/clients', this.clientId]);
  }

  submit(): void {
    if (!this.isAdmin()) {
      return;
    }

    if (!this.clientId || Number.isNaN(this.clientId)) {
      this.toastService.danger('Save Failed', 'Invalid client reference.');
      return;
    }

    if (this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const existingClient = this.client();
    const consentOnFileToReferOut = raw.consentOnFileToReferOut === 'yes';
    const gender = this.toGenderOrNull(raw.gender);
    const race2020 = this.toRace2020OrNull(raw.race2020);
    const ethnicity2020 = this.toEthnicity2020OrNull(raw.ethnicity2020);

    this.saving.set(true);

    this.clientsService
      .updateClientProfile(this.clientId, {
        clientUniqueId: (existingClient?.clientUniqueId ?? raw.clientUniqueId).trim(),
        enrollmentDate: raw.enrollmentDate,
        firstName: raw.firstName.trim(),
        middleName: this.nullIfBlank(raw.middleName),
        lastName: raw.lastName.trim(),
        birthdate: raw.birthdate,
        reasonForInitialContact: raw.reasonForInitialContact.trim(),
        phone: this.nullIfBlank(raw.phone),
        email: this.nullIfBlank(raw.email),
        race2020,
        ethnicity2020,
        gender,
        genderOtherText: gender === 'Other' ? this.nullIfBlank(raw.genderOtherText) : null,
        zipCode: this.nullIfBlank(raw.zipCode),
        consentOnFileToReferOut,
        consentOnFileToReferOutDate: consentOnFileToReferOut
          ? (raw.consentOnFileToReferOutDate || null)
          : null,
      })
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe({
        next: ({ client }) => {
          this.client.set(client);
          this.patchForm(client);
          this.toastService.success('Profile Updated', 'Client profile updated successfully.');
        },
        error: (error) => {
          this.toastService.danger(
            'Update Failed',
            error?.error?.message ?? 'Unable to update client profile right now.',
          );
        },
      });
  }

  private loadClient(): void {
    if (!this.clientId || Number.isNaN(this.clientId)) {
      this.errorMessage.set('Invalid client reference.');
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    this.clientsService
      .getClientDetail(this.clientId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: ({ client }) => {
          this.client.set(client);
          this.patchForm(client);
        },
        error: (error) => {
          this.errorMessage.set(error?.error?.message ?? 'Unable to load client profile right now.');
        },
      });
  }

  private patchForm(client: ClientDetailApiModel): void {
    this.form.patchValue({
      clientUniqueId: client.clientUniqueId ?? '',
      enrollmentDate: this.toDateInputValue(client.enrollmentDate),
      firstName: client.firstName ?? '',
      middleName: client.middleName ?? '',
      lastName: client.lastName ?? '',
      birthdate: this.toDateInputValue(client.birthdate),
      reasonForInitialContact: client.reasonForInitialContact ?? '',
      phone: client.phone ?? '',
      email: client.email ?? '',
      race2020: client.race2020 ?? '',
      ethnicity2020: client.ethnicity2020 ?? '',
      gender: client.gender ?? '',
      genderOtherText: client.genderOtherText ?? '',
      zipCode: client.zipCode ?? '',
      consentOnFileToReferOut: client.consentOnFileToReferOut ? 'yes' : 'no',
      consentOnFileToReferOutDate: this.toDateInputValue(client.consentOnFileToReferOutDate),
    });

    if (this.isAdmin()) {
      this.form.enable({ emitEvent: false });
    } else {
      this.form.disable({ emitEvent: false });
    }
  }

  private nullIfBlank(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }

  private toGenderOrNull(value: string): ClientGender | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    return this.genderOptions.includes(trimmed as ClientGender)
      ? (trimmed as ClientGender)
      : null;
  }

  private toRace2020OrNull(value: string): ClientRace2020 | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    return this.raceOptions.includes(trimmed as ClientRace2020)
      ? (trimmed as ClientRace2020)
      : null;
  }

  private toEthnicity2020OrNull(value: string): ClientEthnicity2020 | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    return this.ethnicityOptions.includes(trimmed as ClientEthnicity2020)
      ? (trimmed as ClientEthnicity2020)
      : null;
  }

  private toDateInputValue(value: string | null): string {
    if (!value) {
      return '';
    }

    const trimmed = value.trim();
    const isoDatePrefix = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed)?.[1];
    if (isoDatePrefix) {
      return isoDatePrefix;
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    return parsed.toISOString().slice(0, 10);
  }
}
